import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ShoppingCart, ChevronDown, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface OrderLineItem {
  id: string;
  orderId: string;
  lineNumber: number;
  description: string;
  quantity: number;
  uom: string;
  unitPrice: string;
  unitCost: string;
  lineTotal: string;
  lineCost: string;
  lineMargin: string;
  procurementStatus: string;
  fulfillmentStatus: string;
  productId: string | null;
  createdAt: string;
  order?: {
    orderNumber: string;
  };
  account?: {
    id: string;
    name: string;
  } | null;
  product?: {
    name: string;
    sku: string;
  } | null;
}

interface OrderGroup {
  orderId: string;
  orderNumber: string;
  accountName: string;
  accountId: string | null;
  items: OrderLineItem[];
  totalExtPrice: number;
  totalExtCost: number;
}

const formatCurrency = (value: string | number | null | undefined) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

function buildOrderGroups(lineItems: OrderLineItem[]): OrderGroup[] {
  const groupMap = new Map<string, OrderGroup>();

  for (const item of lineItems) {
    const orderId = item.orderId;
    if (!groupMap.has(orderId)) {
      groupMap.set(orderId, {
        orderId,
        orderNumber: item.order?.orderNumber || "-",
        accountName: item.account?.name || "-",
        accountId: item.account?.id || null,
        items: [],
        totalExtPrice: 0,
        totalExtCost: 0,
      });
    }
    const group = groupMap.get(orderId)!;
    group.items.push(item);
    group.totalExtPrice += Number(item.lineTotal || 0);
    group.totalExtCost += Number(item.lineCost || 0);
  }

  const groups = Array.from(groupMap.values());
  groups.sort((a, b) => a.orderNumber.localeCompare(b.orderNumber, undefined, { numeric: true }));
  return groups;
}

function OrderGroupRows({
  group,
  isExpanded,
  onToggle,
  onRowClick,
}: {
  group: OrderGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onRowClick: (row: OrderLineItem) => void;
}) {
  const [, setLocation] = useLocation();

  return (
    <>
      <TableRow
        className="cursor-pointer bg-muted/50 border-b border-t border-border/50"
        onClick={onToggle}
      >
        <TableCell colSpan={12}>
          <div className="flex items-center gap-2 py-0.5">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <a
              href={`/orders/${group.orderId}`}
              onClick={(e) => {
                e.stopPropagation();
                setLocation(`/orders/${group.orderId}`);
              }}
              className="text-primary hover:underline font-semibold text-sm"
            >
              {group.orderNumber}
            </a>
            <span className="text-xs text-muted-foreground">
              {group.accountId ? (
                <a
                  href={`/accounts/${group.accountId}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocation(`/accounts/${group.accountId}`);
                  }}
                  className="text-muted-foreground hover:underline"
                >
                  {group.accountName}
                </a>
              ) : (
                group.accountName
              )}
            </span>
            <Badge variant="secondary">{group.items.length}</Badge>
            <span className="text-xs text-muted-foreground ml-auto font-medium">
              {formatCurrency(group.totalExtPrice)}
            </span>
          </div>
        </TableCell>
      </TableRow>
      {isExpanded &&
        group.items.map((row) => (
          <TableRow
            key={row.id}
            onClick={() => onRowClick(row)}
            className="cursor-pointer"
          >
            <TableCell>
              <span className="max-w-[200px] truncate block">
                {row.description}
              </span>
            </TableCell>
            <TableCell>
              {row.product && row.productId ? (
                <span className="font-medium">
                  {row.product.sku}
                </span>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
            <TableCell>
              <StatusBadge status={row.procurementStatus} type="procurement" />
            </TableCell>
            <TableCell>
              <StatusBadge status={row.fulfillmentStatus} type="fulfillment" />
            </TableCell>
            <TableCell className="text-right">
              <span>{row.quantity}</span>
            </TableCell>
            <TableCell>
              <span className="text-muted-foreground capitalize">
                {row.uom}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-muted-foreground">
                {formatCurrency(row.unitCost)}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-muted-foreground">
                {formatCurrency(row.lineCost)}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <span>{formatCurrency(row.unitPrice)}</span>
            </TableCell>
            <TableCell className="text-right">
              <span className="font-medium">
                {formatCurrency(row.lineTotal)}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-green-600 dark:text-green-500">
                {formatCurrency(Number(row.lineTotal || 0) - Number(row.lineCost || 0))}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-muted-foreground">
                {Number(row.lineMargin || 0).toFixed(1)}%
              </span>
            </TableCell>
          </TableRow>
        ))}
    </>
  );
}

function GroupedLineItemsTable({
  lineItems,
  isLoading,
  onRowClick,
}: {
  lineItems: OrderLineItem[];
  isLoading: boolean;
  onRowClick: (row: OrderLineItem) => void;
}) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (orderId: string) => {
    setExpandedGroups((prev) => ({ 
      ...prev, 
      [orderId]: prev[orderId] === false ? true : !(prev[orderId] ?? true) 
    }));
  };

  const groups = buildOrderGroups(lineItems);

  const tableHeaders = [
    "Description", "SKU", "Procurement", "Fulfillment", "Qty", "UOM", 
    "Unit Cost", "Ext. Cost", "Unit Price", "Ext. Price", "Profit", "Margin %"
  ];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {tableHeaders.map((h) => (
                  <TableHead key={h}><Skeleton className="h-4 w-20" /></TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(12)].map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full max-w-[100px]" /></TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {lineItems.length === 0 ? (
        <div className="rounded-md border">
          <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground py-12">
            <ShoppingCart className="h-12 w-12 text-muted-foreground/50" />
            <p>No order line items found</p>
          </div>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Description</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">SKU</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Procurement</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Fulfillment</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Qty</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">UOM</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Unit Cost</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Ext. Cost</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Unit Price</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Ext. Price</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Profit</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">Margin %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => {
                const isExpanded = expandedGroups[group.orderId] ?? true;
                return (
                  <OrderGroupRows
                    key={group.orderId}
                    group={group}
                    isExpanded={isExpanded}
                    onToggle={() => toggleGroup(group.orderId)}
                    onRowClick={onRowClick}
                  />
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {lineItems.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {lineItems.length} line item{lineItems.length !== 1 ? "s" : ""} across {groups.length} order{groups.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

export default function OrderLineItemsPage() {
  const [, setLocation] = useLocation();
  const { sessionId } = useAuth();
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<{ lineItems: OrderLineItem[]; total: number }>({
    queryKey: ["/api/order-line-items", sessionId, { search }],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: "1",
        limit: "2000"
      });
      if (search) params.append('search', search);
      return api.get<{ lineItems: OrderLineItem[]; total: number }>(`/order-line-items?${params.toString()}`, sessionId);
    },
    enabled: !!sessionId,
  });

  const lineItems = data?.lineItems || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Order Line Items"
        actions={
          <div className="flex items-center gap-2">
            <Input
              type="search"
              placeholder="Search line items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-[300px]"
            />
          </div>
        }
      />

      <GroupedLineItemsTable
        lineItems={lineItems}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/orders/${row.orderId}`)}
      />
    </div>
  );
}
